import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useSettings } from "@/lib/settings";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function Analytics() {
  const { settings } = useSettings() || {};
  const currency = settings?.currency || "AUD";
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const income = Object.entries(data.income_by_month || {}).sort().map(([k, v]) => ({ month: k, income: v }));
  const expenses = Object.entries(data.expenses_by_month || {}).sort().map(([k, v]) => ({ month: k, expenses: v }));
  const merged = income.map((row) => ({ ...row, expenses: expenses.find((e) => e.month === row.month)?.expenses || 0 }));
  const projStatus = Object.entries(data.project_status_breakdown || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">The health of your studio — income, invoices, projects, campaigns.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Outstanding" value={money(data.outstanding, currency)} />
        <MetricCard label="Overdue" value={money(data.overdue, currency)} tone="danger" />
        <MetricCard label="Task completion (30d)" value={`${data.task_completion_rate}%`} tone="success" />
        <MetricCard label="Dormant contacts" value={data.dormant_contacts} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="studio-card lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Income vs Expenses (last 6 months)</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Line type="monotone" dataKey="income" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="expenses" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="studio-card">
          <CardHeader><CardTitle className="text-base">Projects by Status</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={projStatus} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                  {projStatus.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="studio-card">
          <CardHeader><CardTitle className="text-base">Campaign Engagement</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: "Sent", value: data.campaign_engagement.sent },
                { name: "Opened", value: data.campaign_engagement.opened },
                { name: "Clicked", value: data.campaign_engagement.clicked },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Bar dataKey="value" fill="hsl(var(--chart-2))" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="studio-card">
          <CardHeader><CardTitle className="text-base">Watch list</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Row label="Task completion (30 days)" value={`${data.tasks_last_30_days.done}/${data.tasks_last_30_days.total}`} />
            <Row label="Follow-ups overdue" value={data.followups_overdue} />
            <Row label="Dormant contacts (60d+)" value={data.dormant_contacts} />
            <Row label="Overdue amount" value={money(data.overdue, currency)} tone={data.overdue > 0 ? "danger" : "default"} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const MetricCard = ({ label, value, tone }) => (
  <Card className="studio-card p-5">
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
    <div className={`mt-2 text-2xl font-semibold tracking-tight ${tone === "danger" ? "text-[hsl(var(--studio-danger))]" : tone === "success" ? "text-[hsl(var(--studio-success))]" : ""}`}>{value}</div>
  </Card>
);
const Row = ({ label, value, tone }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-semibold ${tone === "danger" ? "text-[hsl(var(--studio-danger))]" : ""}`}>{value}</span>
  </div>
);
